<template>
    <DialogLayout :class-name="`schedule-interview-dialog ${isContentOverflow ? 'has-overflow' : ''}`"
        @close="handleClose">
        <template #title>{{ dialogTitle }}</template>
        <template #default>
            <!-- 已接受状态提示 -->
            <div v-if="isAccepted" class="accepted-tip">
                <Icon name="bell" color="#1677FF" class="tip-icon" />
                <span class="tip-text">{{ t('candidate.interviewAcceptedTip') }}</span>
            </div>

            <el-form ref="formRef" :model="formData" :rules="rules" :label-position="'top'"
                class="schedule-interview-form">
                <!-- 面试官 -->
                <el-form-item :label="t('candidate.interviewer')" prop="interviewerId" required>
                    <el-select v-model="formData.interviewerId" :placeholder="t('common.pleaseSelect')" filterable
                        clearable>
                        <template #empty>
                            <EmptyState type="empty" :title="t('common.noData')" />
                        </template>
                        <el-option v-for="option in interviewerOptions" :key="option.id" :label="option.name"
                            :value="option.id" />
                    </el-select>
                </el-form-item>

                <div v-if="isAccepted" class="view-row">
                    <div class="view-item view-item-half">
                        <div class="view-label">{{ t('interview.interviewMethod') }}</div>
                        <div class="view-value">{{ interviewTypeText }}</div>
                    </div>
                    <div class="view-item view-item-half">
                        <div class="view-label">{{ t('candidate.interviewTime') }}</div>
                        <div class="view-value">{{ formData.interviewDuration ? `${formData.interviewDuration}分` :
                            '-'
                            }}</div>
                    </div>
                </div>

                <!-- 面试时间，在已接受状态时显示 -->
                <el-form-item v-if="isAccepted && props.interviewSelected" :label="t('candidate.interviewTime')">
                    <div class="view-value interview-time-highlight">
                        <span class="time-date">{{ props.interviewSelected.date }}({{ props.interviewSelected.week || '-'}})</span>
                        <span class="time-separator">|</span>
                        <span class="time-range">{{ props.interviewSelected.startTime }}</span>
                        <span class="time-range">~</span>
                        <span class="time-range">{{
                            props.interviewSelected.endTime }}</span>
                    </div>
                </el-form-item>

                <!-- 面试方式和面试时长（同一行） -->
                <div v-if="!isAccepted" class="form-row">
                    <el-form-item :label="t('interview.interviewMethod')" prop="interviewType" required
                        class="form-item-half">
                        <el-radio-group v-model="formData.interviewType">
                            <el-radio :value="1">{{ t('interview.online') }}</el-radio>
                            <el-radio :value="2">{{ t('interview.offline') }}</el-radio>
                        </el-radio-group>
                    </el-form-item>

                    <el-form-item :label="t('interview.interviewTime')" prop="interviewDuration" required
                        class="form-item-half">
                        <div class="duration-input-wrapper">
                            <el-input v-model="formData.interviewDuration" type="number"
                                :placeholder="t('common.pleaseEnter')" class="duration-input" :min="1"
                                @input="handleDurationInput">
                                <template #suffix>
                                    <span class="duration-unit">分</span>
                                </template>
                            </el-input>
                        </div>
                    </el-form-item>
                </div>


                <el-form-item v-if="isAccepted" :label="t('candidate.optionalTimeSlots')">
                    <div v-if="formData.interviewSchedule && formData.interviewSchedule.length > 0" class="view-item">
                        <div class="view-value">
                            <div v-for="(slot, index) in formData.interviewSchedule" :key="index"
                                class="schedule-slot-view">
                                <span class="slot-date">{{ slot.date }}({{ slot.week || '-' }})</span>
                                <span class="slot-separator">|</span>
                                <span class="slot-time">{{ slot.startTime }}</span>
                                <span class="slot-time"> ~ </span>
                                <span class="slot-time">{{ slot.endTime }}</span>
                            </div>
                        </div>
                    </div>
                </el-form-item>

                <!-- 可选面试时段 -->
                <el-form-item v-if="!isAccepted" :label="t('candidate.optionalTimeSlots')" prop="interviewSchedule"
                    required>
                    <div v-if="!isAccepted" class="schedule-slots">
                        <div v-for="(slot, index) in formData.interviewSchedule" :key="index"
                            :class="['schedule-slot-item', { 'schedule-slot-item-error': isSlotInvalid(slot) }]">
                            <div class="slot-input-container">
                                <el-date-picker v-model="slot.date" type="date" placeholder="YYYY-MM-DD"
                                    :format="dateDisplayFormat" value-format="YYYY-MM-DD" :prefix-icon="''" :clearable="false" :disabled="isAccepted"
                                    class="slot-date-picker"
                                    @change="(value: string) => handleSlotDateChange(slot, value)" />
                                <span class="slot-separator">|</span>

                                <StepTimePicker v-model="slot.startTime" :disabled="isAccepted"
                                    :show-icon="false" class="slot-time-picker" :editable="true" :auto-commit="true"
                                    :hour-options="getStartHourOptions(slot.date, slot.endTime)"
                                    :minute-options="[0, 15, 30, 45]" @change="handleSlotChange" />
                                <span class="slot-separator">~</span>
                                <StepTimePicker v-model="slot.endTime" :disabled="isAccepted"
                                    :show-icon="false" class="slot-time-picker" :editable="true" :auto-commit="true"
                                    :hour-options="getEndHourOptions(slot.date, slot.startTime)"
                                    :minute-options="(hour: number) => getEndMinuteOptions(slot.date, slot.startTime, hour)"
                                    @change="handleSlotChange" />
                            </div>
                            <el-button type="text" :icon="Close" class="slot-remove-btn"
                                @click="removeScheduleSlot(index)" />
                        </div>
                    </div>
                </el-form-item>
                <el-button v-if="!isAccepted" type="custom" :icon="Plus" class="add-slot-btn" @click="addScheduleSlot">
                    {{ t('common.add') }}
                </el-button>

                <!-- 面试链接（线上时显示） -->
                <!-- <el-form-item v-if="formData.interviewType === 1" :label="t('candidate.interviewLink')"
                        prop="interviewUrl" required>
                        <el-input v-model="formData.interviewUrl" :placeholder="t('common.pleaseEnter')" clearable
                            maxlength="100" />
                    </el-form-item> -->

                <!-- 面试地点（线下时显示，可选） -->
                <!-- <el-form-item v-if="formData.interviewType === 2" :label="t('candidate.interviewAddress')"
                        prop="interviewLocation" required>
                        <el-input v-model="formData.interviewLocation" :placeholder="t('common.pleaseEnter')" clearable
                            maxlength="100" />
                    </el-form-item> -->

                <!-- 备注信息 -->
                <el-form-item :label="t('candidate.remarkInfo')" prop="interviewRemark">
                    <el-input v-model="formData.interviewRemark" type="textarea" :placeholder="t('common.pleaseEnter')"
                        :rows="4" maxlength="100" show-word-limit />
                </el-form-item>
            </el-form>

            <!-- 按钮区域 -->
            <!-- <div class="dialog-buttons">
                    <el-button class="confirm-button-default" @click="handleCancel">
                        {{ t('common.cancel') }}
                    </el-button>
                    <el-button type="primary" :loading="submitting" @click="handleConfirm">
                        {{ t('common.save') }}
                    </el-button>
                </div> -->
        </template>
        <!-- 按钮区域 -->
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button type="primary" :loading="submitting" @click="handleConfirm">
                {{ t('common.save') }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted, onUnmounted, nextTick, watch, onUpdated } from 'vue'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { Plus, Close } from '@element-plus/icons-vue'
import StepTimePicker from '@/components/basic-components/step-time-picker/index.vue'
import DialogLayout from './dialog-layout.vue'
import EmptyState from '@/components/basic-components/empty-state/EmptyState.vue'
import { Icon } from '@/components/basic-components/icon'
import type { ScheduleInterviewDialogProps, InterviewScheduleSlot, InterviewerOption } from './schedule-interview-dialog-types'
import { scheduleInterviewApi, getCandidateAccountApi } from '@/api/candidate'
import { updateInterviewerApi } from '@/api/interview'
import { useRoutePermission } from '@/hooks/useRoutePermission'
import { UserRole } from '@/router/config/roles'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'
import 'dayjs/locale/ja'
import 'dayjs/locale/zh-cn'
const { t, locale, tm } = useI18n()
const props = defineProps<ScheduleInterviewDialogProps>()

const { userRole } = useRoutePermission()

// 判断是否是面试官
const isInterviewer = computed(() => userRole.value === UserRole.INTERVIEWER)

const emit = defineEmits<{
    (e: 'confirm'): void
    (e: 'cancel'): void
    (e: 'close'): void
}>()

const formRef = ref<FormInstance>()
const submitting = ref(false)
const isViewMode = ref(props.viewMode || false)
const isUnmounted = ref(false)
// 存储表单验证错误状态
const scheduleValidationError = ref(false)
// 内容是否超过 500px
const isContentOverflow = ref(false)

// 面试官选项列表
const interviewerOptions = ref<InterviewerOption[]>([])

// 获取面试方式文本
const interviewTypeText = computed(() => {
    if (formData.interviewType === 1) return t('interview.online')
    if (formData.interviewType === 2) return t('interview.offline')
    return '-'
})

// 获取面试官选项
const fetchInterviewerOptions = async () => {
    try {
        const response = await getCandidateAccountApi({ roleCodesExclude: ['ROLE_BUSINESS_OPERATION'] })
        if (response && Array.isArray(response)) {
            interviewerOptions.value = response.map((item: Record<string, unknown>) => ({
                id: (item.id as number) || 0,
                name: (item.name as string) || ''
            }))
        } else if (response && response.list && Array.isArray(response.list)) {
            interviewerOptions.value = response.list.map((item: Record<string, unknown>) => ({
                id: (item.id as number) || 0,
                name: (item.name as string) || ''
            }))
        }
    } catch (error) {
        console.error('获取面试官选项失败:', error)
    }
}

// 对话框标题
const dialogTitle = computed(() => {
    if (isViewMode.value) {
        const stepName = props.stepName || ''
        return stepName ? t('candidate.interviewInfoTitle', { stepName }) : t('candidate.interviewInfo')
    }
    const stepName = props.stepName || ''
    const isEdit = !!props.data && props.status !== 5 && props.status !== 6
    if (isEdit) {
        return stepName ? t('candidate.adjustInterviewTitle', { stepName }) : t('candidate.adjustInterview')
    } else {
        return stepName ? t('candidate.scheduleInterviewTitle', { stepName }) : t('candidate.scheduleInterview')
    }
})

// 是否已接受面试（status === 3 表示已接受）
const isAccepted = computed(() => props.status === 3)

// 表单数据
const formData = reactive<{
    interviewerId?: number
    interviewType?: number
    interviewDuration?: number
    interviewSchedule: InterviewScheduleSlot[]
    // interviewUrl?: string
    // interviewLocation?: string
    interviewRemark?: string
}>({
    interviewerId: undefined,
    interviewType: 1, // 默认线上
    interviewDuration: 60,
    interviewSchedule: [],
    // interviewUrl: '',
    // interviewLocation: '',
    interviewRemark: ''
})

const getWeekLabel = (dateStr?: string) => {
    if (!dateStr) return ''
    const [year, month, day] = dateStr.split('-').map(Number)
    if (!year || !month || !day) return ''
    const date = new Date(year, month - 1, day)
    if (Number.isNaN(date.getTime())) return ''
    const dayIndex = date.getDay()
    const labels = tm('candidate.weekShort') as string[]
    return Array.isArray(labels) ? labels[dayIndex] || '' : ''
}

const updateSlotWeek = (slot: InterviewScheduleSlot, dateStr?: string) => {
    slot.week = getWeekLabel(dateStr ?? slot.date)
}

const dateDisplayFormat = computed(() => {
    const isJa = String(locale.value || '').toLowerCase().startsWith('ja')
    return isJa ? 'YYYY-MM-DD (ddd)' : 'YYYY-MM-DD (ddd)'
})

const syncDayjsLocale = () => {
    const isJa = String(locale.value || '').toLowerCase().startsWith('ja')
    dayjs.locale(isJa ? 'ja' : 'zh-cn')
}

// 表单验证规则
const rules = computed<FormRules>(() => ({
    interviewerId: [
        { required: true, message: t('interview.pleaseSelectInterviewer'), trigger: 'change' }
    ],
    interviewType: [
        { required: true, message: t('interview.pleaseSelectInterviewMethod'), trigger: 'change' }
    ],
    interviewDuration: [
        { required: true, message: t('interview.pleaseEnterInterviewDuration'), trigger: 'blur' },
        {
            validator: (_rule: unknown, value: unknown, callback: (error?: Error) => void) => {
                if (value === '' || value === null || value === undefined) {
                    callback()
                    return
                }
                const numValue = Number(value)
                if (isNaN(numValue)) {
                    callback(new Error(t('interview.pleaseEnterValidNumber')))
                    return
                }
                if (numValue <= 0) {
                    callback(new Error(t('interview.durationMustBeGreaterThanZero')))
                    return
                }
                callback()
            },
            trigger: 'blur'
        }
    ],
    // interviewLocation: [
    //     { required: true, message: t('interview.pleaseEnterInterviewAddress'), trigger: 'blur' }
    // ],
    interviewSchedule: [
        {
            required: true,
            validator: (rule, value, callback) => {
                if (!value || value.length === 0) {
                    callback(new Error(t('interview.pleaseAddAtLeastOneTimeSlot')))
                } else {
                    // 验证每个时段是否完整
                    const incomplete = value.some((slot: InterviewScheduleSlot) => 
                        !slot.date || !slot.startTime || !slot.endTime
                    )
                    if (incomplete) {
                        callback(new Error(t('interview.pleaseCompleteAllTimeSlotInfo')))
                        return
                    }
                    
                    // 验证每个时段的时长不能小于面试时长
                    if (formData.interviewDuration) {
                        const durationMinutes = formData.interviewDuration
                        const invalidSlot = value.some((slot: InterviewScheduleSlot) => {
                            if (!slot.startTime || !slot.endTime) return false
                            
                            // 计算时段时长（分钟）
                            const [startHour, startMin] = slot.startTime.split(':').map(Number)
                            const [endHour, endMin] = slot.endTime.split(':').map(Number)
                            const startMinutes = startHour * 60 + startMin
                            const endMinutes = endHour * 60 + endMin
                            const slotDuration = endMinutes - startMinutes
                            
                            return slotDuration < durationMinutes
                        })
                        
                        if (invalidSlot) {
                            callback(new Error(t('interview.timeSlotMustNotBeLessThanDuration')))
                            return
                        }
                    }
                    
                    callback()
                }
            },
            trigger: 'change'
        }
    ]
    // interviewUrl: [
    //     {
    //         required: true,
    //         validator: (rule, value, callback) => {
    //             if (formData.interviewType === 1 && !value) {
    //                 callback(new Error(t('interview.pleaseEnterInterviewLink')))
    //             } else {
    //                 callback()
    //             }
    //         },
    //         trigger: 'blur'
    //     }
    // ]
}))

const createEmptySlot = (): InterviewScheduleSlot => ({
    date: '',
    startTime: '',
    endTime: '',
    week: ''
})

// 添加面试时段
const addScheduleSlot = () => {
    formData.interviewSchedule.push(createEmptySlot())
}

// 删除面试时段
const removeScheduleSlot = async (index: number) => {
    formData.interviewSchedule.splice(index, 1)
    // 删除时段后清除该字段的错误校验，避免因删除操作把其余未填写的时段标红
    scheduleValidationError.value = false
    await nextTick()
    formRef.value?.clearValidate('interviewSchedule')
}

// 检查单个 slot 是否有错误
const isSlotInvalid = (slot: InterviewScheduleSlot): boolean => {
    // 检查 slot 是否完整
    // if (!slot.date || !slot.startTime || !slot.endTime) {
    //     return true
    // }
    
    // 检查时段时长是否小于面试时长
    if (formData.interviewDuration) {
        const durationMinutes = formData.interviewDuration
        const [startHour, startMin] = slot.startTime.split(':').map(Number)
        const [endHour, endMin] = slot.endTime.split(':').map(Number)
        const startMinutes = startHour * 60 + startMin
        const endMinutes = endHour * 60 + endMin
        const slotDuration = endMinutes - startMinutes
        
        if (slotDuration < durationMinutes) {
            return true
        }
    }
    
    // 如果有表单验证错误，且当前 slot 不完整，则显示错误
    if (scheduleValidationError.value && (!slot.date || !slot.startTime || !slot.endTime)) {
        return true
    }
    
    return false
}

// 处理时段变化（触发表单验证）
const handleSlotChange = async () => {
    // 如果组件已卸载，不执行验证
    if (isUnmounted.value) return
    
    // 使用 nextTick 确保在下一个 tick 执行验证，避免在组件卸载时执行
    await nextTick()
    
    // 再次检查组件是否已卸载
    if (isUnmounted.value || !formRef.value) return
    
    try {
        // 触发表单验证
        await formRef.value.validateField('interviewSchedule')
        // 验证通过，清除错误状态
        scheduleValidationError.value = false
    } catch (error) {
        // 验证失败，设置错误状态
        scheduleValidationError.value = true
        // 忽略组件卸载时的验证错误
        if (!isUnmounted.value) {
            console.error('验证字段失败:', error)
        }
    }
}

const handleSlotDateChange = async (slot: InterviewScheduleSlot, value?: string) => {
    updateSlotWeek(slot, value)
    await handleSlotChange()
}

// 处理面试时长输入，不允许负数、0、小数，最大值1440
const handleDurationInput = (value: string | number) => {
    if (value === '' || value === null || value === undefined) {
        formData.interviewDuration = undefined
        //触发表单验证
        if (formRef.value && formData.interviewSchedule.length > 0) {
            formRef.value.validateField('interviewSchedule')
        }
        return
    }
    let numValue = Number(value)
    if (isNaN(numValue)) {
        formData.interviewDuration = undefined
        // 触发表单验证
        if (formRef.value && formData.interviewSchedule.length > 0) {
            formRef.value.validateField('interviewSchedule')
        }
        return
    }
    // 不允许小数，自动取整
    numValue = Math.floor(numValue)
    // 如果输入的是负数或0，自动修正为1
    if (numValue <= 0) {
        numValue = 1
    }
    // 如果超过1440，自动修正为1440
    if (numValue > 1440) {
        numValue = 1440
    }
    formData.interviewDuration = numValue
    // 触发表单验证（验证时段时长是否满足要求）
    if (formRef.value && formData.interviewSchedule.length > 0) {
        formRef.value.validateField('interviewSchedule')
    }
}

// 禁用过去的日期，只允许选择今天及未来的日期
const disabledPastDate = (time: Date) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return time.getTime() < today.getTime()
}

// 获取开始时间的小时选项
const getStartHourOptions = (date?: string, maxTime?: string): number[] => {
    const minTime = getStartTimeMin(date)
    const [minHour] = minTime.split(':').map(Number)
    const [maxHour] = (maxTime || '23:59').split(':').map(Number)
    
    const hours: number[] = []
    for (let i = minHour; i <= maxHour; i++) {
        hours.push(i)
    }
    return hours
}

// 获取结束时间的小时选项
const getEndHourOptions = (date?: string, startTime?: string): number[] => {
    const minTime = getEndTimeMin(date, startTime)
    const [minHour] = minTime.split(':').map(Number)
    
    const hours: number[] = []
    for (let i = minHour; i <= 23; i++) {
        hours.push(i)
    }
    return hours
}

// 获取结束时间的分钟选项（根据小时和开始时间限制）
const getEndMinuteOptions = (date?: string, startTime?: string, endHour?: number): number[] => {
    const minutes = [0, 15, 30, 45]
    
    if (!date || !startTime || endHour === undefined) {
        return minutes
    }
    
    const minTime = getEndTimeMin(date, startTime)
    const [minHour, minMinute] = minTime.split(':').map(Number)
    
    // 如果选择的是最小时间的小时，需要过滤掉小于最小分钟的选项
    if (endHour === minHour) {
        return minutes.filter(m => m >= minMinute)
    }
    
    return minutes
}

// 获取开始时间的最小值（如果选择的是今天，从当前时间开始；否则从00:00开始）
const getStartTimeMin = (date?: string): string => {
    return '00:00'
    if (!date) return '00:00'
    
    const selectedDate = new Date(date)
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]
    const selectedDateStr = selectedDate.toISOString().split('T')[0]
    
    // 如果选择的是今天，从当前时间开始（向上取整到15分钟）
    if (selectedDateStr === todayStr) {
        const now = new Date()
        const hours = now.getHours()
        const minutes = now.getMinutes()
        // 向上取整到下一个15分钟
        const roundedMinutes = Math.ceil(minutes / 15) * 15
        const finalHours = roundedMinutes >= 60 ? hours + 1 : hours
        const finalMinutes = roundedMinutes >= 60 ? 0 : roundedMinutes
        
        // 如果超过23:59，返回23:59
        if (finalHours >= 24) {
            return '23:59'
        }
        
        return `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`
    }
    
    return '00:00'
}

// 获取结束时间的最小值（如果选择的是今天且已选择开始时间，从开始时间开始；否则从00:00开始）
const getEndTimeMin = (date?: string, startTime?: string): string => {
    if (!date) return '00:00'
    
    const selectedDate = new Date(date)
    const today = new Date()
    const todayStr = today.toISOString().split('T')[0]
    const selectedDateStr = selectedDate.toISOString().split('T')[0]
    
    // 如果选择的是今天，需要确保结束时间不早于当前时间
    if (selectedDateStr === todayStr) {
        const now = new Date()
        const nowHours = now.getHours()
        const nowMinutes = now.getMinutes()
        // 向上取整到下一个15分钟
        const roundedMinutes = Math.ceil(nowMinutes / 15) * 15
        const finalHours = roundedMinutes >= 60 ? nowHours + 1 : nowHours
        const finalMinutes = roundedMinutes >= 60 ? 0 : roundedMinutes
        
        if (finalHours >= 24) {
            return '23:59'
        }
        
        const minTime = `${String(finalHours).padStart(2, '0')}:${String(finalMinutes).padStart(2, '0')}`
        
        // 如果开始时间已选择，返回开始时间和当前时间的较大者
        if (startTime) {
            return startTime > minTime ? startTime : minTime
        }
        
        return minTime
    }
    
    // 如果开始时间已选择，结束时间不能早于开始时间
    return startTime || '00:00'
}

// 检测内容高度是否超出对话框高度
const checkContentHeight = () => {
    nextTick(() => {
        const dialogContent = document.querySelector('.schedule-interview-dialog .dialog-content') as HTMLElement
        if (dialogContent) {
            isContentOverflow.value = dialogContent.scrollHeight > (window.innerHeight - 78 - 75 - 100)
        }
    })
}

// 监听窗口大小变化的处理函数
const handleResize = () => {
    checkContentHeight()
}

// 初始化表单数据（回显）
onMounted(() => {
    // 获取面试官选项
    fetchInterviewerOptions()
    
    if (props.data) {
        formData.interviewerId = props.data.interviewerId
        formData.interviewType = props.data.interviewType ?? 1
        formData.interviewDuration = props.data.interviewDuration
        // 无回显时段时也默认给一个空选择器，省去用户先点「追加」
        formData.interviewSchedule = props.data.interviewSchedule?.length
            ? [...props.data.interviewSchedule]
            : [createEmptySlot()]
        formData.interviewSchedule.forEach((slot) => {
            updateSlotWeek(slot)
        })
        // formData.interviewUrl = props.data.interviewUrl || ''
        // formData.interviewLocation = props.data.interviewLocation || ''
        formData.interviewRemark = props.data.interviewRemark || ''
    } else {
        // 新建时默认加载一个空的时段选择器
        formData.interviewSchedule = [createEmptySlot()]
    }
    
    // 检测内容高度
    checkContentHeight()
    
    // 监听窗口大小变化，重新检测高度
    window.addEventListener('resize', handleResize)
})

// 当内容更新时重新检测高度
onUpdated(() => {
    checkContentHeight()
})

// 监听表单数据变化，重新检测高度
watch(() => [formData.interviewSchedule.length, formData.interviewDuration], () => {
    checkContentHeight()
}, { deep: true })

watch(locale, () => {
    syncDayjsLocale()
}, { immediate: true })

// 组件卸载时设置标志并移除事件监听
onUnmounted(() => {
    isUnmounted.value = true
    window.removeEventListener('resize', handleResize)
})

// 确认保存
const handleConfirm = async () => {
    if (!formRef.value) return

    try {
        await formRef.value.validate()
        submitting.value = true

        // 过滤掉空的时段
        const validSchedule = formData.interviewSchedule.filter(
            slot => slot.date && slot.startTime && slot.endTime
        )

        // 验证时间段是否过期：如果日期是今天，检查开始时间是否已经过了当前时间
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const currentHour = now.getHours()
        const currentMinute = now.getMinutes()
        const currentTimeMinutes = currentHour * 60 + currentMinute
        
        const expiredSlot = validSchedule.find((slot) => {
            if (!slot.date || !slot.startTime) return false
            
            // 解析日期
            const slotDate = new Date(slot.date)
            const slotDateOnly = new Date(slotDate.getFullYear(), slotDate.getMonth(), slotDate.getDate())
            
            // 判断日期是否是今天
            const isToday = slotDateOnly.getTime() === today.getTime()
            
            if (isToday) {
                // 如果是今天，检查开始时间是否已经过了当前时间
                const [startHour, startMin] = slot.startTime.split(':').map(Number)
                const startTimeMinutes = startHour * 60 + startMin
                
                // 如果开始时间小于等于当前时间，则已过期
                return startTimeMinutes <= currentTimeMinutes
            }
            
            return false
        })
        
        // if (expiredSlot) {
        //     ElMessage.error(t('interview.timeSlotExpired'))
        //     submitting.value = false
        //     return
        // }
        
        // 调用 API 保存
        if (isAccepted.value) {
            await updateInterviewerApi({
                interviewId: props.interviewId,
                stepId: props.stepId,
                interviewerId: formData.interviewerId!,
                interviewRemark: formData.interviewRemark || ''
            })
        } else {
            await scheduleInterviewApi({
                interviewId: props.interviewId,
                stepId: props.stepId,
                interviewerId: formData.interviewerId!,
                interviewType: formData.interviewType!,
                interviewDuration: formData.interviewDuration!,
                interviewSchedule: validSchedule,
                // interviewUrl: formData.interviewType === 1 ? formData.interviewUrl : undefined,
                // interviewLocation: formData.interviewType === 2 ? formData.interviewLocation : undefined,
                interviewRemark: formData.interviewRemark || undefined
            })
        }

        ElMessage.success(t('common.saveSuccess'))
        submitting.value = false
        emit('confirm')
    } catch (error: unknown) {
        submitting.value = false
        
        // 如果是表单验证错误（字段验证错误格式：{ fieldName: [{ message, field }] }），不显示 API 错误提示
        if (error && typeof error === 'object') {
            const errorObj = error as Record<string, unknown>
            // 检查是否是字段验证错误格式：有字段名作为键，且值是数组
            const isFieldValidationError = Object.keys(errorObj).some(key => {
                const value = errorObj[key]
                return Array.isArray(value) && value.length > 0 && 
                       typeof value[0] === 'object' && 
                       (('message' in value[0]) || ('field' in value[0]))
            })
            if (isFieldValidationError) {
            return
            }
        }
        
        // API 调用失败
        const errorMessage = error && typeof error === 'object' && 'message' in error 
            ? String((error as { message: string }).message) 
            : t('common.saveFailed')
        console.error('保存失败:', error)
        ElMessage.error(errorMessage)
    }
}

// 切换到编辑态
const handleEdit = () => {
    isViewMode.value = false
}

// 取消
const handleCancel = () => {
    emit('cancel')
}

// 关闭
const handleClose = () => {
    emit('close')
}
</script>

<style scoped lang="scss">
.accepted-tip {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 8px 12px;
    margin: 20px 0;
    margin-top:0;
    background-color: #E6F4FF;
    border-radius: 4px;

    .tip-icon {
        flex-shrink: 0;
        margin-top: 5px;
    }

    .tip-text {
        flex: 1;
        font-size: 13px;
        line-height: 22px;
        color: #1677FF;
    }
}

.interview-time-display {
    //width: 270px;
    display: flex;
    align-items: center;
   // gap: 8px;
    padding: 6px 8px;
    background-color: #DBF1F1;
    border-radius: 8px;
    font-size: 13px;
    line-height: 17px;
    font-weight: normal;
    .time-date {
        color: #009E99;
        padding-right: 10px;
    }

    .time-separator {
        padding:0 8px;
        color: #009E99;
    }

    .time-range { 
        color: #009E99;
    }
}

.schedule-interview-form {

    :deep(.el-form-item__label) {
        font-size: 14px;
        font-weight: 500;
        color: #212429;
        line-height: 22px;

        &::before {
            display: none;
        }
    }

    :deep(.el-select) {
        width: 100%;
    }

    .form-row {
        display: flex;
        gap: 16px;
        align-items: flex-start;
        margin-bottom: 18px;

        .form-item-half {
            flex: 1;
            margin-bottom: 0;
        }
    }

    .duration-input-wrapper {
        .duration-input {
            width: 100%;
        }

        .duration-unit {
            font-size: 14px;
            color: #86909C;
        }
    }

    .schedule-slots {
        display: flex;
        flex-direction: column;
        gap: 12px;

        .schedule-slot-item {
            display: flex;
            align-items: center;
            gap: 0;
            position: relative;
            background: #F8F9FF;
            border-radius: 4px;
            border: 1px solid transparent;
            transition: border-color 0.2s;

            &.schedule-slot-item-error {
                border-color: #F56C6C;
            }

            .slot-input-container {
                width: 240px;
                display: flex;
                align-items: center;
                gap: 6px;
                min-height: 30px;
                background-color: #F8F9FF;
                border-radius: 4px;

                .slot-date-picker {
                    :deep(.el-input__wrapper) {
                        box-shadow: none !important;
                        padding: 0 !important;
                        background-color: #F8F9FF !important;
                        border: none !important;
                    }

                    :deep(.el-input__inner) {
                        height: 30px!important;
                        padding: 0 !important;
                        border: none !important;
                        font-size: 13px;
                        color: #495057;
                        background-color: transparent !important;
                    }
                }


                .time-select-group {
                    display: flex;
                    align-items: center;

                    .time-hour-select {
                        width: 26px;

                        :deep(.el-select__wrapper) {
                            box-shadow: none !important;
                            padding: 0 !important;
                            background-color: transparent !important;
                            border: none !important;
                            font-size: 13px !important;
                        }

                        :deep(.el-input__inner) {
                            padding: 0 !important;
                            border: none !important;
                            font-size: 13px;
                            text-align: center;
                            background-color: transparent !important;
                        }

                        :deep(.el-select__caret) {
                            display: none !important;
                        }
                    }

                    .time-minute-select {
                        width: 30px;

                        :deep(.el-select__wrapper) {
                            box-shadow: none !important;
                            padding: 0 !important;
                            background-color: transparent !important;
                            border: none !important;
                            font-size: 13px !important;
                        }

                        :deep(.el-input__inner) {
                            padding: 0 !important;
                            border: none !important;
                            font-size: 13px;
                            text-align: center;
                            background-color: transparent !important;
                        }

                        :deep(.el-select__caret) {
                            display: none !important;
                        }
                    }

                    .time-separator {
                        color: #495057;
                        font-size: 13px;
                        padding: 0 2px;
                    }
                }

                .slot-time-picker {
                    flex-shrink: 0;
                }

                .slot-separator {
                    font-size: 13px;
                    color: #C9D3D9;
                    white-space: nowrap;
                    flex-shrink: 0;
                }
            }

            .slot-remove-btn {
                flex-shrink: 0;
                padding: 8px;
                color: #86909C;
                font-size: 14px;
                width: 32px;
                height: 32px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
                border: none;
                background: transparent;

                &:hover {
                    color: #606266;
                }

                :deep(.el-icon) {
                    font-size: 14px;
                }
            }
        }

        
    }

        .add-slot-btn {
            align-self: flex-start;
            border: none;
            background: transparent;
            color: var(--el-color-primary);
            font-size: 14px;
            font-weight: normal;
            line-height: 22px;
            height: 22px;
            padding: 0;
            margin: 0;
            margin-bottom: 10px;
        }

    :deep(.el-textarea) {
        .el-textarea__inner {
            font-size: 14px;
            color: #495057;
            line-height: 22px;
            resize: none;

            &::placeholder {
                color: #C9D3D9;
            }
        }
    }

    :deep(.el-input__count) {
        font-size: 12px;
        color: #86909C;
        background: transparent;
    }
}

.view-item {
    margin-bottom: 20px;

    &:last-child {
        margin-bottom: 0;
    }

    .view-label {
        font-size: 14px;
        font-weight: 500;
        color: #86909C;
        margin-bottom: 8px;
    }

    .view-value {
        font-size: 13px;
        color: #495057;
        line-height: 17px;
        word-break: break-all;
    }

    &.view-item-half {
        flex: 0 0 calc(50% - 8px);
    }
}

.view-row {
    display: flex;
    gap: 16px;
}

 .schedule-slot-view {
     display: inline-flex;
     align-items: center;
     padding: 6px 8px;
     background: #F8F9FF;
     border-radius: 8px;
     font-size: 13px;
     color: #495057;
     margin-bottom: 8px;
     //margin-right: 8px;

     &:last-child {
         margin-bottom: 0;
     }

     .slot-date {
         padding-right: 8px;
     }

     .slot-separator {
         padding: 0 8px;
         color: #86909C;
     }

     .slot-time {
         padding-left: 8px;
     }
 }

  .interview-time-highlight {
      display: inline-flex;
      align-items: center;
      padding: 6px 8px;
      background: #DBF1F1;
      border-radius: 8px;
      font-size: 13px;
      line-height: 17px;
      color: #009E99;

      .time-date {
          padding-right: 8px;
          color: #009E99;
      }

      .time-separator {
          padding: 0 8px;
          color: #86909C;
      }

      .time-range {
          padding-left: 8px;
          color: #009E99;
      }
  }

// 展示态样式
.view-mode-container {
    padding: 20px 0;
    .tag-view {
        border-radius: 462px;
        opacity: 1;
        display: flex;
        flex-direction: row;
        justify-content: center;
        align-items: center;
        padding: 0px 8px;
        gap: 4px;
        background: #EEF2F7;
        font-size: 14px;
        font-weight: normal;
        color: #88929B;
        width: 75px;
    }
}

:deep(.schedule-interview-dialog) {
    width: 512px;

    
}
</style>

<style lang="scss">
.schedule-interview-dialog {
    width: 512px !important;

    // 全局样式覆盖日期选择器和时间选择器的背景色
    .schedule-slot-item {
        .slot-date-picker {
            width: 110px !important;
            min-width: 110px !important;
        }

        .slot-time-picker {
            width: 55px !important;
            min-width: 55px !important;
        }

        .slot-date-picker {
            
            .el-input__wrapper {
                
                box-shadow: none !important;
                padding: 0 !important;
                background-color: transparent !important;
                border: none !important;
            }

            .el-input__inner {
                text-align: center !important;
                background-color: transparent !important;
                font-size: 13px !important;
            }

            // 隐藏前缀图标
            .el-input__prefix {
                display: none !important;
            }

            .el-input__prefix-inner {
                display: none !important;
            }
        }

    }
}
  // 下拉选项样式 - 修复选中状态对勾和文字重叠问题
  .el-select-dropdown__list {
      .el-select-dropdown__item {
          &.is-selected {

              // 给文字内容添加右边距，避免与对勾重叠
              span {
                  padding-right: 10px !important;
              }
          }
      }
  }
</style>

<style lang="scss">
// 只针对 schedule-interview-dialog 的滚动布局
.schedule-interview-dialog {
    .dialog-content {
        // 对话框已居中，计算：视口高度 - header高度(78px) - footer高度(75px) - 上下边距(100px)
        max-height: calc(100vh - 78px - 75px - 100px);
        overflow-y: auto;
    }
    
    // 按钮区域样式
    .dialog-footer {
        display: flex;
            justify-content: flex-end;
            gap: 12px;
            padding: 20px 30px;
        
            .el-button {
                min-width: 80px;
            } 
    }
          &.has-overflow .dialog-footer {
              box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
          }

        &.has-overflow .dialog-header {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }

                  
                 
     
}
</style>

